import { useCallback, useEffect, useRef, useState } from 'react'
import { avisarCoordenadasGrandes, distanciaAOrigem, temCoordenadasGrandes } from '@viewer/lib/georef/coordenadasGrandes'
import * as THREE from 'three'
import * as OBC from '@thatopen/components'
import * as OBF from '@thatopen/components-front'
import * as WEBIFC from 'web-ifc'
import type { FragmentsModel } from '@thatopen/fragments'
import { RenderedFaces } from '@thatopen/fragments'

import { useAppStore } from '@core/store'
import { readFileBytes } from '@core/lib/readFile'
import { motivoParaRecusarIfc, readSchemaFromIfcBytes } from '@viewer/lib/ifcSchema'
import {
  ESTILO_DAS_ZONAS,
  viewerApi,
  type ResultadoDaAbertura,
} from '@viewer/lib/viewerApi'
import { largarModelosDescartados } from './modelosLocais'
import { rawIfc } from '@viewer/lib/rawIfc'
import {
  applyGeoPlacement,
  applyGeoTransform,
  IDENTITY_PLACEMENT,
  placementDeFragments,
  placementsDiffer,
  primeGeometryBoxCache,
  resolveRobustFitBox,
  type GeoPlacement,
} from '@viewer/lib/geoTransform'
import {
  guardarLeitura,
  guardarSitio,
  leituraGuardada,
  lerConversaoDoMapa,
  lerLocalizacaoDoSitio,
  lerUnidadeDoProjecto,
  type LocalizacaoDoSitio,
} from '@viewer/lib/georef/leituraDoMapa'
import { guardarVerificacoes, lerDadosDeGeorref, type DadosDeGeorref } from '@viewer/lib/georef/verificacoesGeorref'
import { verificarGeorrefDoModelo } from '@viewer/lib/georef/verificarModelo'
import { colocacaoPeloSitio, registoDaColocacao } from '@viewer/lib/georef/colocacaoPeloSitio'
import { metrosDoCrs } from '@viewer/map/crsDefs'
import { conferirCrs } from '@viewer/lib/georef/conferirCrs'
import { rememberHandle } from '@core/session/ifcHandles'
import { ifcPersistence } from '@core/session/ifcPersistence'
import { fragmentPersistence } from '@core/session/fragmentPersistence'
import { isFeatureEnabled } from '@core/features'
import { installEnhancedCameraControls } from '@viewer/lib/enhancedCameraControls'
import * as paintEngine from '@viewer/lib/paintEngine'
import {
  buildEdgeIdAttribute,
  patchEdgeMaterial,
  setPermanentlyHidden as setEdgePermanentlyHidden,
} from '@viewer/lib/edgeVisibility'
import { extractIfcFromZip, looksLikeZip } from '@viewer/lib/ifcZip'
import { textoDaGeorreferenciacao } from '@viewer/lib/georef/textoParaGeorref'
import {
  installEdgesSnapshotPatch,
  type EdgesSourceCache,
  type GeometryWithEdgesCache,
} from '@viewer/canvas/edgesSnapshot'
import { vigiarBotaoDireito } from '@viewer/canvas/arrastoDireito'
import { aplicarEstiloApresentacao } from '@viewer/canvas/estiloApresentacao'
import {
  canOpenFragments,
  canOpenPointClouds,
  isFragmentFilename,
  isPointCloudFilename,
} from '@core/session/modelFileTypes'

// Cópia das posições/ids das malhas do Fragments antes de o array ser
// apagado no envio para a GPU (ver edgesSnapshot.ts para o porquê e
// para a razão de a cópia se fazer no onUpload e não no setAttribute).
installEdgesSnapshotPatch()

/** True when ANY unique localId in the snapshotted `id` attribute is in
 *  the spatial-container set. We're aggressive on purpose: if a mesh
 *  contains even one IfcSpace / IfcBuildingStorey / etc. it gets its
 *  edges suppressed. The alternative (building per-item edges so we
 *  can hide just the container items) is a much bigger lift, and the
 *  cost (losing duct edges in tiles that happen to be batched with a
 *  space) is acceptable because spaces are typically toggled hidden
 *  anyway and those ducts are visible without any edge rendering at
 *  all (their shaded surface is enough to read the geometry). */
function meshContainsSpatialContainer(
  idArray: ArrayLike<number>,
  containerIds: Set<number>,
): boolean {
  if (containerIds.size === 0) return false
  for (let i = 0; i < idArray.length; i++) {
    if (containerIds.has(idArray[i])) return true
  }
  return false
}

/** Get the per-vertex `id` array for a mesh, preferring the snapshot
 *  (survives GPU upload) but falling back to the live attribute if
 *  Fragments populated it via a code path that bypassed our
 *  setAttribute monkey-patch. Returns null when neither is available. */
function getMeshIdArray(m: THREE.Mesh): ArrayLike<number> | null {
  const snap = (m.geometry as GeometryWithEdgesCache)?.__vn2rEdgesSrc
  if (snap?.id && snap.id.length > 0) return snap.id
  const liveId = m.geometry?.attributes?.id as
    | THREE.BufferAttribute
    | undefined
  const arr = liveId?.array as ArrayLike<number> | undefined
  if (arr && arr.length > 0) return arr
  return null
}

// Force web-ifc to use the single-thread WASM build. Reason: under our
// COOP/COEP headers `self.crossOriginIsolated` is true, so web-ifc's
// `Init()` takes the multi-thread branch and spawns pthread workers via
// `new Worker(_scriptName)`. In Vite's ESM dev server `_scriptName` is
// `undefined`, so the worker URL becomes `/undefined`, the dev server
// answers with index.html, and the IfcAPI hangs waiting for pthreads
// that will never start. `@thatopen/fragments@3.4`'s IfcImporter calls
// `Init()` with no args, so we cannot pass `forceSingleThread` per call;
// patching the prototype is the only injection point.
const originalIfcApiInit = WEBIFC.IfcAPI.prototype.Init
WEBIFC.IfcAPI.prototype.Init = async function (customLocateFileHandler) {
  return originalIfcApiInit.call(this, customLocateFileHandler, true)
}

type World = OBC.SimpleWorld<
  OBC.SimpleScene,
  OBC.SimpleCamera,
  OBF.RendererWith2D
>

interface ViewerRefs {
  container: HTMLDivElement
  components: OBC.Components
  world: World
  ifcLoader: OBC.IfcLoader
  fragments: OBC.FragmentsManager
  highlighter: OBF.Highlighter
  models: Map<string, FragmentsModel>
}

export interface LoadIfcOptions {
  replace?: boolean
  /**
   * FileSystemFileHandle that produced this File. When present we
   * persist it in IndexedDB keyed by sha256 so a future Open Session
   * can re-read the same file without prompting (the user still has
   * to grant permission once, then the browser remembers it).
   */
  handle?: FileSystemFileHandle | null
}

export interface UseViewer {
  containerRef: React.RefObject<HTMLDivElement | null>
  loading: boolean
  stage: string | null
  error: string | null
  loadIfcFile: (file: File, options?: LoadIfcOptions) => Promise<ResultadoDaAbertura>
}

/** Tira do mapa local os modelos que o viewerApi já descartou (Remover,
 *  Fechar sessão, Substituir). A regra pura está em modelosLocais.ts. */
function podarModelosDescartados(models: Map<string, FragmentsModel>): void {
  largarModelosDescartados(models, (id) => viewerApi.getModel(id) !== undefined)
}

/** Um id que nenhum modelo aberto usa: o próprio nome, ou "Nome (2).ifc",
 *  "Nome (3).ifc", e assim por diante. Conta com o mapa local, com o
 *  viewerApi e com a lista do store, porque durante um Substituir o
 *  modelo a sair ainda está nos três. */
export function idDeModeloLivre(
  nome: string,
  locais: Map<string, unknown> = new Map(),
): string {
  const ocupados = new Set<string>([
    ...locais.keys(),
    ...Array.from(viewerApi.iterModels(), ([id]) => id),
    ...useAppStore.getState().models.map((m) => m.id),
  ])
  if (!ocupados.has(nome)) return nome
  const ponto = nome.lastIndexOf('.')
  const base = ponto > 0 ? nome.slice(0, ponto) : nome
  const ext = ponto > 0 ? nome.slice(ponto) : ''
  for (let n = 2; ; n += 1) {
    const candidato = `${base} (${n})${ext}`
    if (!ocupados.has(candidato)) return candidato
  }
}

/** Liberta as arestas dos tiles que o motor já apagou. O Fragments tira
 *  o tile do `model.object` e liberta a geometria e o material dele, mas
 *  não os filhos que a aplicação lhe pendurou (as arestas). */
function libertarArestasDeTilesApagados(malhas: Set<THREE.Mesh>): void {
  for (const m of malhas) {
    if (m.parent) continue
    for (const filho of m.children) {
      if ((filho as THREE.LineSegments).isLineSegments) {
        ;(filho as THREE.LineSegments).geometry.dispose()
      }
    }
    malhas.delete(m)
  }
}

// Bootstraps the ThatOpen viewer stack (Components + World + Renderer +
// IfcLoader + FragmentsManager) inside the container ref and returns
// imperatives to load an IFC. Disposes everything on unmount.
export function useViewer(): UseViewer {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const refsRef = useRef<ViewerRefs | null>(null)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [stage, setStage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Broadcast loading state to the ribbon's LoadingPill (which lives
  // outside the viewer subtree and so cannot read these states via
  // props or useViewer). Fired on every transition.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('vn2r:viewer-stage', {
        detail: { loading, stage },
      }),
    )
  }, [loading, stage])

  const addModel = useAppStore((s) => s.addModel)
  const removeModel = useAppStore((s) => s.removeModel)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let cancelled = false
    let refs: ViewerRefs | null = null
    let shiftKeyCleanupRef: (() => void) | null = null
    let selectionUnsubRef: (() => void) | null = null
    let enhancedCameraCleanupRef: (() => void) | null = null

    // StrictMode + ThatOpen: components.dispose() does not remove the
    // <canvas> SimpleRenderer appended to the container, so the second
    // mount stacks a new canvas on top of the zombie one. With both at
    // h-full in normal flow the live canvas gets pushed out of view and
    // the user only sees the disposed one. Wipe the container before
    // (re)building the world.
    container.replaceChildren()

    const init = async () => {
      const components = new OBC.Components()
      const worlds = components.get(OBC.Worlds)
      const world = worlds.create<
        OBC.SimpleScene,
        OBC.SimpleCamera,
        OBF.RendererWith2D
      >()

      world.scene = new OBC.SimpleScene(components)
      // RendererWith2D piggy-backs a CSS2DRenderer on the standard
      // SimpleRenderer. Required so ThatOpen Mark / DimensionLine label
      // / snap picker (all CSS2DObjects under the hood) actually attach
      // their HTML elements to the DOM. With a plain SimpleRenderer the
      // measurement cotas, endpoint dots and snap previews never appear.
      world.renderer = new OBF.RendererWith2D(components, container)
      // ThatOpen >= 3.4.4 desenha um wordmark "That Open Company" como
      // overlay do renderer por defeito. Opt-out oficial e documentado
      // pela propria lib (MIT); o credito ao stack ThatOpen mantem-se
      // no Sobre/Diagnostico. Cast: a propriedade nao existe nas versoes
      // anteriores (no-op inofensivo), e mantemo-la para quando o motor
      // voltar a subir.
      ;(world.renderer as { showLogo?: boolean }).showLogo = false
      world.camera = new OBC.SimpleCamera(components)
      // The CSS2D layer sits on top of the WebGL canvas; route every
      // pointer event back to the WebGL canvas underneath so selection,
      // marquee, orbit, etc. keep working.
      const css2d = world.renderer.three2D.domElement
      css2d.style.pointerEvents = 'none'
      css2d.style.position = 'absolute'
      css2d.style.top = '0'
      css2d.style.left = '0'

      components.init()

      world.scene.setup()
      const initialBg = useAppStore.getState().bgMode === 'light' ? '#cbd5e1' : '#1c1d24'
      world.scene.three.background = new THREE.Color(initialBg)

      // FragmentsModel BIMMesh uses MeshLambertMaterial; without
      // lights it renders pure black and is indistinguishable from
      // the dark background. SimpleScene.setup() does not guarantee
      // adequate lighting, so add an ambient + directional pair.
      // Tuning rationale: lower ambient + higher directional + a
      // second directional from the opposite angle makes faces of
      // different orientation read with very different brightness.
      // That gradient IS the silhouette/contour the user sees, so
      // reinforcing it is the cheapest way to "perceber melhor os
      // contornos" without touching edges or setColor (which leak).
      const ambient = new THREE.AmbientLight(0xffffff, 0.4)
      const keyLight = new THREE.DirectionalLight(0xffffff, 1.4)
      keyLight.position.set(50, 100, 50)
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.5)
      fillLight.position.set(-40, 60, -30)
      const backLight = new THREE.DirectionalLight(0xffffff, 0.35)
      backLight.position.set(0, -50, -80)
      world.scene.three.add(ambient, keyLight, fillLight, backLight)
      {
        // Sempre, mesmo desligado: deixa a luz de hemisfério criada antes
        // de haver materiais, para ligar o estilo não recompilar shaders.
        const st = useAppStore.getState()
        aplicarEstiloApresentacao(world.scene.three, st.estiloApresentacao, st.bgMode)
      }

      world.camera.controls.setLookAt(20, 20, 20, 0, 0, 0)
      if (isFeatureEnabled('enhancedCamera')) {
        enhancedCameraCleanupRef = installEnhancedCameraControls(
          world.camera.controls,
        )
      }

      // O rato.
      //
      // Esquerdo: seleccionar, e arrastar no vazio faz a janela de
      // seleção, como no Revit. Não roda, e é de propósito: é o mesmo
      // gesto que a janela, e não cabem os dois.
      //
      // Direito: arrastar RODA. Pedido do Rui, para não ser preciso
      // Shift + roda para rodar. Um clique direito sem arrastar continua
      // a abrir o menu de contexto e a acabar as medições; quem trata
      // disso pergunta ao `arrastoDireito` se foi clique ou arrasto,
      // senão o menu abria no fim de cada rotação.
      //
      // Roda: carregar e arrastar desloca, Shift + roda roda, como no
      // Revit, e fica como estava. Rodar a roda faz zoom.
      //
      // O Shift troca a roda por mutação directa: o camera-controls lê o
      // campo no início de cada arrasto, por isso basta mudá-lo a tempo.
      // Valores do CameraControls.ACTION: NONE=0, ROTATE=1, TRUCK=2.
      const cc = world.camera.controls as unknown as {
        mouseButtons: { left: number; right: number; middle: number }
      }
      cc.mouseButtons.left = 0 // NONE
      cc.mouseButtons.right = 1 // ROTATE
      cc.mouseButtons.middle = 2 // TRUCK (pan)
      const onShiftDown = (e: KeyboardEvent) => {
        if (e.key === 'Shift') cc.mouseButtons.middle = 1 // ROTATE
      }
      const onShiftUp = (e: KeyboardEvent) => {
        if (e.key === 'Shift') cc.mouseButtons.middle = 2 // TRUCK
      }
      window.addEventListener('keydown', onShiftDown)
      window.addEventListener('keyup', onShiftUp)
      const pararVigiaDoDireito = vigiarBotaoDireito()
      shiftKeyCleanupRef = () => {
        window.removeEventListener('keydown', onShiftDown)
        window.removeEventListener('keyup', onShiftUp)
        pararVigiaDoDireito()
      }

      const grids = components.get(OBC.Grids)
      grids.create(world)
      viewerApi.setGrids(grids)
      // Apply the persisted grid toggle on the SimpleGrid we just
      // created. SimpleGrid is a shader Mesh, not a GridHelper, so we
      // toggle its `.visible` getter directly.
      const initialGridVisible = useAppStore.getState().gridVisible
      for (const grid of grids.list.values()) {
        grid.visible = initialGridVisible
      }

      const fragments = components.get(OBC.FragmentsManager)
      // Serve the worker locally instead of the default
      // FragmentsManager.getWorker() which fetches from unpkg.com at
      // runtime. unpkg is blocked by our COOP/COEP headers and would
      // also mean the tool doesn't work offline. File is copied to
      // public/thatopen/ by scripts/sync-vendor.mjs on postinstall.
      fragments.init('/thatopen/fragments-worker.mjs')

      // FragmentsModels.update() has an internal throttle: if called
      // within `maxUpdateRate` ms of the last call, it returns
      // immediately without processing. We need every `update(true)`
      // we issue (especially the one right after a model load) to
      // atually run, so disable the throttle entirely.
      fragments.core.settings.maxUpdateRate = 0

      // FragmentsModel renders tiled meshes on demand: nothing is
      // visible until fragments.core.update() runs. Pump it on camera
      // changes only. Do NOT pump on every render frame: update() has
      // an internal throttle (settings.maxUpdateRate) and a per-frame
      // call saturates it, causing later force-updates after model
      // load to be skipped.
      world.camera.controls.addEventListener('rest', () => {
        void fragments.core.update(true)
      })
      world.camera.controls.addEventListener('control', () => {
        void fragments.core.update()
      })
      // Os planos de corte mudam sem a câmara mexer: quem os muda pede
      // aqui uma passagem de vista para o worker os receber.
      viewerApi.setActualizadorDosModelos(() => {
        void fragments.core.update()
      })

      const ifcLoader = components.get(OBC.IfcLoader)
      // Hook the IfcImporter init so we can tweak BOTH the web-ifc
      // settings (CIRCLE_SEGMENTS, smoothness of circular sweeps)
      // AND the GeometryProcessSettings (the post-tessellation face
      // / vertex thresholds that the importer uses to dedupe and
      // weld). Default thresholds discard small / thin geometry (
      // typical of MEP pipe walls, fitting joints, etc.), so the
      // importer outputs gappy meshes for HVAC. Lowering them keeps
      // those triangles. Done via the event so the values are set
      // BEFORE the first load() call begins tessellating.
      ifcLoader.onIfcImporterInitialized.add((importer) => {
        importer.webIfcSettings.CIRCLE_SEGMENTS = 32
        // GeometryProcessSettings: `threshold` é o número de vértices a
        // partir do qual uma malha NÃO passa pelo optimizador de shells;
        // `precision`, `normalPrecision` e `planePrecision` são FACTORES
        // de arredondamento (1e6 = 1 µm), não tolerâncias.
        //
        // Até à beta.167 baixava-se tudo com Math.min(valor, 1e-5),
        // julgando que eram tolerâncias. Na 3.4.3 o único efeito real era
        // o threshold (nenhuma malha optimizada). A partir da 3.4.6 a
        // precision arredonda as caixas: deu as caixas de ±100000 do
        // issue #243, que era nosso. Na 3.4.8 arredonda também os vértices
        // a 100 km, e a geometria colapsava (área zero em tudo).
        //
        // Fica só o threshold a 0: todas as malhas em bruto, que é o que a
        // 3.4.3 fazia de facto. Medido a 07/10/2026 (3.4.8, MEP 6587
        // elementos): os mesmos triângulos que a 3.4.3, caixas a menos de
        // 0,1 mm, a geometria que o ensaio de setembro validou contra o
        // IfcOpenShell. Os valores por omissão dariam as mesmas formas
        // com triângulos reorganizados e .frag 10-25% menor.
        importer.geometryProcessSettings.threshold = 0
        // IfcSpatialZone (compartimentos corta-fogo, zonas térmicas,
        // zonas de segurança) não está na lista de classes com geometria
        // do importador: ele converte o IfcSpace e deixa as zonas de
        // fora, sem geometria e sem sequer um item no modelo. Com isto
        // entram como os espaços: volumes, escondidos por omissão pelo
        // "Esconder spaces", visíveis quando se desliga.
        importer.classes.elements.add(WEBIFC.IFCSPATIALZONE)
        // Os types. Por omissão o importador só guarda quatro relações
        // (propriedades, materiais, agregação, contenção) e nenhuma
        // classe de type: o IfcRelDefinesByType perdia-se, e o painel
        // dizia "instance-only" a TODOS os elementos, mesmo num IFC com
        // 39 types (o modelo de exemplo). Guarda-se a relação, nos dois
        // sentidos (IsTypedBy na instância, Types no type), e as classes
        // de type, incluindo os IfcDoorStyle/IfcWindowStyle do IFC2x3.
        importer.relations.set(WEBIFC.IFCRELDEFINESBYTYPE, {
          forRelating: 'Types',
          forRelated: 'IsTypedBy',
        })
        for (const [nome, codigo] of Object.entries(WEBIFC)) {
          if (typeof codigo !== 'number') continue
          if (nome === 'IFCRELDEFINESBYTYPE') continue
          if (/^IFC[A-Z0-9]+TYPE$/.test(nome) || nome === 'IFCDOORSTYLE' || nome === 'IFCWINDOWSTYLE') {
            importer.classes.abstract.add(codigo)
          }
        }
      })
      await ifcLoader.setup({
        autoSetWasm: false,
        wasm: { path: '/wasm/', absolute: true },
      })
      // Belt-and-braces: also patch the live settings in case the
      // event already fired (depending on internal init order in
      // @thatopen/components).
      ifcLoader.settings.webIfc.CIRCLE_SEGMENTS = 32
      if (cancelled) return

      // Highlighter is set up but does NOT auto-handle clicks. We do
      // raycasting + selection ourselves in the canvas pointer
      // handlers so we can read modifier keys (Ctrl/Shift) and
      // support marquee selection. The highlighter then mirrors the
      // store's selectedTargets/hover via highlightByID.
      const highlighter = components.get(OBF.Highlighter)
      highlighter.setup({
        world,
        autoHighlightOnClick: false,
        selectMaterialDefinition: {
          color: new THREE.Color('#f59e0b'),
          opacity: 1,
          transparent: false,
          renderedFaces: RenderedFaces.TWO,
        },
      })
      // 'hover' style for soft pre-selection overlay.
      highlighter.styles.set('hover', {
        color: new THREE.Color('#fbbf24'),
        opacity: 0.5,
        transparent: true,
        renderedFaces: RenderedFaces.TWO,
      })
      // 'ghost' style: translucent grey overlay applied to every
      // visible non-selected item when ghost mode is on. Opacity comes
      // from the persisted store value so the user's preference
      // survives reloads. Edges still render at full opacity
      // (LineSegments aren't part of the highlighter material).
      highlighter.styles.set('ghost', {
        color: new THREE.Color('#9ca3af'),
        opacity: useAppStore.getState().ghostOpacity,
        transparent: true,
        renderedFaces: RenderedFaces.TWO,
      })
      // 'fade' style: makes the selected items translucent. We tried
      // preserveOriginalMaterial:true (to keep the BIM colour) but the
      // Highlighter v3.4 silently ignores it on multi-style overlays,
      // resulting in "no visible change" complaints. A near-neutral
      // grey reads as "transparent" reliably across schemes; the
      // colour washes out a bit but the silhouette + edges stay.
      // As IfcSpatialZone: volumes violeta translúcidos, para se
      // distinguirem dos IfcSpace (cinzentos) e não taparem o que têm
      // dentro. Ver viewerApi.marcarZonas.
      highlighter.styles.set(ESTILO_DAS_ZONAS, {
        color: new THREE.Color('#8b5cf6'),
        opacity: 0.22,
        transparent: true,
        renderedFaces: RenderedFaces.TWO,
      })
      highlighter.styles.set('fade', {
        color: new THREE.Color('#cbd5e1'),
        opacity: useAppStore.getState().transparencyOpacity,
        transparent: true,
        renderedFaces: RenderedFaces.TWO,
      })
      viewerApi.setHighlighter(highlighter)
      viewerApi.setWorld(world)
      // (Old highlighter onAfterUpdate / Zustand selection-subscribe
      // repaint hooks were here. They existed to recover from the
      // Fragments setColor slot being wiped by resetHighlight on
      // de-selection. The new paintEngine writes per-item colours into
      // a custom shader uniform texture that the highlighter doesn't
      // touch, so the wipe-and-restore dance is no longer needed.)
      // Required so THREE.Material clipping planes (set later by the
      // section box) take effect on the model meshes.
      world.renderer.three.localClippingEnabled = true

      refs = {
        container,
        components,
        world,
        ifcLoader,
        fragments,
        highlighter,
        models: new Map(),
      }
      refsRef.current = refs
      setReady(true)
    }

    init().catch((err) => {
      console.error('[useViewer] init failed', err)
      if (!cancelled) setError(String(err))
    })

    return () => {
      cancelled = true
      setReady(false)
      shiftKeyCleanupRef?.()
      shiftKeyCleanupRef = null
      enhancedCameraCleanupRef?.()
      enhancedCameraCleanupRef = null
      selectionUnsubRef?.()
      selectionUnsubRef = null
      const r = refsRef.current
      refsRef.current = null
      viewerApi.setHighlighter(null)
      viewerApi.setWorld(null)
      viewerApi.setGrids(null)
      viewerApi.setActualizadorDosModelos(null)
      if (r) {
        // Os já descartados pelo viewerApi não se descartam outra vez.
        largarModelosDescartados(r.models, (id) => viewerApi.getModel(id) !== undefined)
        for (const [id, model] of r.models) {
          viewerApi.unregisterModel(id)
          rawIfc.unloadModel(id)
          useAppStore.getState().closePopupsForModel(id)
          // A lista tem de acompanhar a cena: um modelo descartado aqui
          // ficava na barra lateral sem nada desenhado.
          useAppStore.getState().removeModel(id)
          void model.dispose()
        }
        r.models.clear()
        r.components.dispose()
      }
      // Belt-and-braces: components.dispose() leaves the renderer canvas
      // attached to the container. Strip whatever remains so the next
      // mount starts from an empty container.
      container.replaceChildren()
    }
  }, [])

  const carregarAgora = useCallback(
    async (
      file: File,
      options?: LoadIfcOptions,
    ): Promise<ResultadoDaAbertura> => {
      const r = refsRef.current
      if (!r || !ready) {
        setError('Viewer ainda não está pronto.')
        return { ok: false, erro: 'Viewer ainda não está pronto.' }
      }
      const replace = options?.replace ?? false
      const handle = options?.handle ?? null
      // O mapa local guarda o que este hook carregou, mas quem descarta
      // é o viewerApi.disposeModel (Remover, Fechar sessão), que não o
      // vê. Poda-se aqui o que já não está registado: sem isto o mapa
      // nunca voltava a ficar vazio e o modelo seguinte não era
      // enquadrado.
      podarModelosDescartados(r.models)

      // Nuvens de pontos e IFC5 desviam-se AQUI, antes de tudo o resto.
      //
      // Estavam mais abaixo, depois do bloco que, em modo substituir,
      // descarrega todos os IFC abertos. Escolher um PTS no "Substituir"
      // com um IFC aberto apagava o IFC para abrir uma coisa que nem o
      // substitui: uma nuvem vive ao lado do modelo, não no lugar dele.
      // Era o defeito que o Rui reportou, ao contrário.
      //
      // Quem escolhe um destes no Abrir modelo, ou o arrasta para a
      // janela, quer vê-lo, e não tem de saber que há um painel à parte.
      if (isPointCloudFilename(file.name) && canOpenPointClouds()) {
        setError(null)
        const { pointCloudApi } = await import('@viewer/point-cloud/pointCloudApi')
        useAppStore.getState().setPointCloudPanelOpen(true)
        const entrada = await pointCloudApi.abrir(file)
        if (!entrada) {
          return {
            ok: false,
            erro: pointCloudApi.ultimoErro() ?? 'A leitura da nuvem de pontos foi cancelada.',
          }
        }
        return { ok: true, modelId: null }
      }
      if (/\.ifcx$/i.test(file.name)) {
        setError(null)
        try {
          const { ifcxApi } = await import('@viewer/ifcx/ifcxApi')
          await ifcxApi.abrir(file)
          useAppStore.getState().setIfcxPanelOpen(true)
          return { ok: true, modelId: null }
        } catch (err) {
          const erro = err instanceof Error ? err.message : 'Não foi possível abrir esse IFC5.'
          setError(erro)
          return { ok: false, erro }
        }
      }

      setError(null)
      setLoading(true)
      setStage('A ler ficheiro')

      // Substituir: os modelos abertos só saem DEPOIS de o novo estar
      // convertido (ver mais abaixo, antes do scene.add). Saíam aqui,
      // antes de se ler o ficheiro, e um ficheiro que não abria deixava
      // a cena vazia.
      const aSubstituir = replace ? Array.from(r.models.keys()) : []

      // Whether to refit the camera after this load. In replace mode
      // (or when this is the first model loaded) we frame it; in add
      // mode (federation) we preserve the user's current view.
      const shouldFit = replace || r.models.size === 0

      // O modelo deste pedido, quando já existe: se algo falhar depois de
      // o motor o criar, sai em vez de ficar na cena sem entrada na lista.
      let modeloCriado: FragmentsModel | null = null

      try {
        const isFragmentSource = isFragmentFilename(file.name)
        if (isFragmentSource && !canOpenFragments()) {
          throw new Error(
            'Abrir Fragments nativos está desligado nesta instalação: um ' +
              '.frag não traz o IFC, e o IDS, o BoQ, a Tabela, o IFCEdit e o ' +
              'assistente leem o IFC. Liga a flag openFragments se quiseres ' +
              'mesmo abri-lo, sabendo disso.',
          )
        }

        const rawBuffer = await readFileBytes(file)
        let bytes = new Uint8Array(rawBuffer)
        let displayName = file.name

        // .ifczip support: detect a zip wrapper either by extension or
        // by PK magic bytes, extract the .ifc inside, and continue
        // exactly as if the user had picked the inner .ifc directly.
        // We replace the bytes (used for hash + cache + load) and the
        // displayName (shown in the sidebar / used as model id).
        const looksZipExt =
          file.name.toLowerCase().endsWith('.ifczip') ||
          file.name.toLowerCase().endsWith('.zip')
        if (!isFragmentSource && (looksZipExt || looksLikeZip(bytes))) {
          setStage('A descompactar IFC zip')
          try {
            const inner = extractIfcFromZip(bytes)
            // Re-wrap into a fresh Uint8Array<ArrayBuffer> so TS sees
            // the narrower type the rest of the function expects.
            // Avoids any chance of a SharedArrayBuffer slipping in
            // from fflate's output type.
            bytes = new Uint8Array(inner.bytes)
            displayName = inner.name
          } catch (err) {
            throw err instanceof Error
              ? err
              : new Error(String(err))
          }
        }

        // Recusar à cabeça um ficheiro vazio ou que não é IFC, antes de
        // o converter: a mensagem diz o que está mal, em vez do erro do
        // conversor.
        if (!isFragmentSource) {
          const recusa = motivoParaRecusarIfc(bytes, displayName)
          if (recusa) throw new Error(recusa)
        }

        // O id do modelo é o nome do ficheiro, e um segundo ficheiro com
        // o mesmo nome entra como "Nome (2).ifc". Com o mesmo id, o
        // segundo substituía o primeiro nos registos (aqui, no viewerApi
        // e na lista do Fragments) e o primeiro ficava desenhado para
        // sempre, sem se poder tirar. No Substituir também: o modelo a
        // sair ainda existe quando o novo é criado, e descartá-lo pelo
        // mesmo id apagava o novo no worker.
        const modelId = idDeModeloLivre(displayName, r.models)

        // Independent copy retained for the parallel raw IFC parser.
        // The IfcLoader below may transfer/consume `bytes`, so we
        // can't reuse them.
        const rawCopy = bytes.slice()

        // SHA-256 of the (extracted) IFC bytes so BCF viewpoints can
        // identify which file they depend on. Computed up-front
        // (cheap) before we hand the bytes to the IfcLoader.
        const hashBuf = await crypto.subtle.digest(
          'SHA-256',
          bytes.buffer.slice(
            bytes.byteOffset,
            bytes.byteOffset + bytes.byteLength,
          ) as ArrayBuffer,
        )
        const sha256 = Array.from(new Uint8Array(hashBuf))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')

        // O mesmo ficheiro ja aberto (pelo SHA-256): nao abre outra vez.
        // Duas aberturas do mesmo modelo davam "Nome (2).ifc" sobreposto
        // ao primeiro e, em paralelo, deixavam a sessao presa.
        if (!replace) {
          const ja = useAppStore.getState().models.find((m) => m.sha256?.toLowerCase() === sha256)
          if (ja) {
            setStage(null)
            return { ok: true, modelId: ja.id }
          }
        }

        // Keep original IFC bytes and the derived Fragments cache separate.
        // This preserves rawIfc editing/inspection while allowing a repeat load
        // to skip the expensive IFC tessellation step.
        if (isFragmentSource) {
          void fragmentPersistence.save({
            sourceSha256: sha256,
            filename: displayName,
            sourceFormat: 'frag',
            buffer: rawCopy,
          })
        } else {
          void ifcPersistence
            .save({ sha256, filename: displayName, bytes: rawCopy })
            .catch(() => {})
        }

        setStage(isFragmentSource ? 'A ler Fragments' : 'A ler schema')
        const schema = isFragmentSource
          ? null
          : readSchemaFromIfcBytes(bytes)

        let model: FragmentsModel
        let usedDerivedCache = false
        if (isFragmentSource) {
          model = await r.fragments.core.load(rawCopy, {
            modelId,
            camera: r.world.camera.three,
          })
        } else {
          const cached = isFeatureEnabled('nativeFragments')
            ? await fragmentPersistence.load(sha256)
            : null
          if (cached) {
            setStage('A abrir cache Fragments')
            try {
              model = await r.fragments.core.load(
                await fragmentPersistence.buffer(cached),
                {
                  modelId,
                  camera: r.world.camera.three,
                },
              )
              usedDerivedCache = true
              void fragmentPersistence.touch(sha256)
            } catch (cacheError) {
              console.warn(
                '[useViewer] invalid Fragments cache; rebuilding from IFC',
                cacheError,
              )
              await fragmentPersistence.remove(sha256)
              try {
                await r.fragments.core.disposeModel(modelId)
              } catch {
                // A failed load may not have registered a model.
              }
              setStage('A reconverter IFC para Fragments')
              model = await r.ifcLoader.load(bytes, true, modelId, {
                processData: {
                  progressCallback: (progress, data) => {
                    const pct = Math.round(progress * 100)
                    setStage(`${data.process} ${data.state} ${pct}%`)
                  },
                },
              })
            }
          } else {
            setStage('A converter IFC para Fragments')
            model = await r.ifcLoader.load(bytes, true, modelId, {
              processData: {
                progressCallback: (progress, data) => {
                  const pct = Math.round(progress * 100)
                  setStage(`${data.process} ${data.state} ${pct}%`)
                },
              },
            })
          }

          if (!usedDerivedCache && isFeatureEnabled('nativeFragments')) {
            void model
              .getBuffer(false)
              .then((buffer) =>
                fragmentPersistence.save({
                  sourceSha256: sha256,
                  filename: displayName.replace(/\.ifc$/i, '.frag'),
                  sourceFormat: 'ifc',
                  buffer,
                }),
              )
              .catch((cacheError) =>
                console.warn(
                  '[useViewer] could not create Fragments cache',
                  cacheError,
                ),
              )
          }
        }

        modeloCriado = model
        // Substituir: só agora, com o novo convertido, saem os anteriores.
        // `manterBytes`: reabrir o mesmo ficheiro dá o mesmo SHA, e os
        // bytes acabados de guardar para o novo não podem sair da cache
        // de recuperação com o antigo.
        for (const id of aSubstituir) {
          await viewerApi.disposeModel(id, { manterBytes: sha256, manterNome: displayName })
          r.models.delete(id)
        }

        setStage('A adicionar à cena')
        r.world.scene.three.add(model.object)
        r.models.set(model.modelId, model)
        viewerApi.registerModel(model.modelId, model)

        // Drop a previous STORE entry with the same id (re-loading the
        // same file) BEFORE the geo block below. removeModel also purges
        // geoBaselines/geoOverrides for the id; calling it after
        // setGeoBaseline (as it used to, next to addModel) wiped the
        // freshly-read IfcMapConversion baseline and the Onda B effect
        // then reset the model to the origin. Moving the dedup here keeps
        // the same semantics (stale entry + stale geo replaced) without
        // clobbering the new baseline.
        removeModel(model.modelId)

        // Onda A da federação georreferenciada: ler IfcMapConversion
        // do modelo e aplicar como transform do root antes do
        // fit-to-bbox e antes da câmara. Modelos sem MapConversion
        // ficam na origem (comportamento histórico). Modelos com
        // MapConversion aparecem na posição real do mundo, e dois
        // IFCs do mesmo projecto com origens diferentes
        // (e.g. Revit + Civil 3D) caem no sítio um relativo ao outro.
        try {
          const crs = await model.getCRS().catch(() => null)
          // Nome do CRS projectado (ex.: "EPSG:3763") para o editor de
          // posição e o mapa. null quando o IFC não declara.
          useAppStore.getState().setCrsMeta(model.modelId, crs?.name ?? null)
          // Unidade de comprimento do projeto (m=1, mm=0.001). O
          // conversor Fragments normaliza a geometria para METROS, por
          // isso o Scale do IfcMapConversion (convenção de unidade, ex.
          // 0.001 num ficheiro em mm) NÃO pode ser aplicado cru como
          // escala geométrica ao object: encolheria o modelo 1000x.
          // Passamos o fator para o dividir. Basta varrer o prefixo:
          // IFCSIUNIT/IFCUNITASSIGNMENT vivem no topo da secção de dados.
          // A conversão lida como a norma manda (leituraDoMapa.ts): o
          // Scale, o MapUnit do IfcProjectedCRS (ou a unidade do projecto
          // quando falta) e a unidade dos eixos do CRS, quando se conhece.
          // Se o ficheiro se contradiz, não se aplica nada sem escolha.
          let baseline: GeoPlacement = IDENTITY_PLACEMENT
          let sitio: LocalizacaoDoSitio | null = null
          // Para as verificações da georreferenciação (mais abaixo, quando
          // já se sabe a distância da geometria à origem).
          let dadosGeorref: DadosDeGeorref | null = null
          if (isFragmentSource) {
            // Um .frag não traz o texto do IFC: só o que o Fragments leu, sem
            // a unidade do projecto. O Scale fica por aplicar (ver
            // placementDeFragments); o painel diz porquê.
            baseline = placementDeFragments(crs?.mapConversion).placement ?? IDENTITY_PLACEMENT
          } else {
            // Lê-se o texto, e não só o que o Fragments devolve: o getCRS()
            // dele só existe com IfcProjectedCRS, e um IFC2X3 (ePset_
            // MapConversion) ou um IfcMapConversionScaled ficavam na origem.
            const decoder = new TextDecoder('latin1')
            const cabeca = decoder.decode(bytes.subarray(0, 2_000_000))
            let todo: string | null = bytes.length > 2_000_000 ? null : cabeca
            // Não o ficheiro inteiro numa cadeia: acima de 64 MB só as
            // entidades que a leitura usa (ver textoParaGeorref.ts).
            const textoTodo = () => (todo ??= textoDaGeorreferenciacao(bytes))
            // O prefixo chega quando traz a conversão E a unidade do
            // projecto. Os IFC do Revit 2026 escrevem a IfcUnitAssignment
            // depois dos primeiros 2 MB: a conversão lia-se, a unidade não,
            // e a georreferenciação não se aplicava (Clínica, 21/10/2026).
            const prefixoCompleto = bytes.length <= 2_000_000 || lerUnidadeDoProjecto(cabeca).unidade !== null
            const textoDaLeitura = () => (prefixoCompleto ? cabeca : textoTodo())
            const ler = (metrosDoCrs: number | null) =>
              lerConversaoDoMapa(textoDaLeitura(), { metrosDoCrs }) ??
              (bytes.length > 2_000_000 && prefixoCompleto ? lerConversaoDoMapa(textoTodo(), { metrosDoCrs }) : null)
            let leitura = ler(await metrosDoCrs(crs?.name))
            // O CRS de um ePset só se conhece depois de o ler: confere-se
            // a unidade dos eixos dele numa segunda leitura.
            const nomeLido = leitura?.crs?.nome ?? null
            if (leitura && !crs?.name && nomeLido) {
              const m = await metrosDoCrs(nomeLido)
              if (m !== null) leitura = ler(m)
            }
            if (leitura) await conferirCrs(leitura)
            if (!crs?.name && nomeLido) useAppStore.getState().setCrsMeta(model.modelId, nomeLido)
            if (!leitura) {
              sitio =
                lerLocalizacaoDoSitio(textoDaLeitura()) ??
                (bytes.length > 2_000_000 && prefixoCompleto ? lerLocalizacaoDoSitio(textoTodo()) : null)
            }
            guardarSitio(model.modelId, sitio)
            guardarLeitura(model.modelId, leitura)
            // O IfcSite costuma vir no princípio; sem ele no prefixo lê-se o
            // resto (o texto inteiro, ou o reduzido acima de 64 MB).
            dadosGeorref = lerDadosDeGeorref(
              bytes.length > 2_000_000 && (!prefixoCompleto || !/=\s*IFCSITE\s*\(/.test(cabeca)) ? textoTodo() : cabeca,
            )
            if (leitura?.escalaGeometrica != null && leitura.origemEmMetros) {
              baseline = {
                eastings: leitura.origemEmMetros[0],
                northings: leitura.origemEmMetros[1],
                orthogonalHeight: leitura.origemEmMetros[2],
                rotationDeg: leitura.rotacaoGraus,
                scale: leitura.escalaGeometrica,
              }
            } else if (leitura && leitura.alternativas.length > 0) {
              useAppStore.getState().pushToast(
                `${model.modelId}: a georreferenciação contradiz-se e não foi aplicada. Escolhe a leitura em Informações do modelo, Georreferenciação.`,
                { type: 'info' },
              )
            }
          }
          // Save baseline first so the sidebar "Reset" button has a
          // target. Then look for an override in two places, in
          // priority order:
          //   1. session-restore: when Open Session re-loads a model
          //      whose manifest carried a placement_override (Onda C),
          //      we find the matching VnSessionModel by sha256 and
          //      apply it. Match is on sha256 (not filename) because
          //      the loader's model.modelId == filename which may
          //      have changed between save and load.
          //   2. in-memory: the user may have already set an override
          //      via the sidebar BEFORE this loader run (rare but
          //      possible: drag/drop while another model already
          //      had a manual placement).
          useAppStore.getState().setGeoBaseline(model.modelId, baseline)
          // Recentramento do conversor: o Fragments translada a
          // geometria para perto da origem local (mesmo offsets
          // modestos) e devolve a translação t em getCoordinates() =
          // [tx,ty,tz, ...basis]. O applyGeoPlacement precisa de t para
          // aplicar o placement às coordenadas do FICHEIRO (sem isto o
          // modelo ficava deslocado por R(θ)·t face ao mapa). Guardado
          // em userData ANTES de qualquer applyGeoPlacement.
          let recentre: [number, number, number] = [0, 0, 0]
          try {
            const coords = await model.getCoordinates()
            if (Array.isArray(coords) && coords.length >= 3) {
              recentre = [coords[0], coords[1], coords[2]]
            }
          } catch {
            /* sem coordinates: recentre 0 */
          }
          model.object.userData.vn2rRecentre = recentre
          // Coordenadas de mapa nas posições dos elementos (com ou sem
          // IfcMapConversion): avisa-se, porque é má prática em qualquer
          // versão do IFC (viewer/lib/georef/coordenadasGrandes.ts).
          {
            const distanciaM = distanciaAOrigem(recentre)
            if (temCoordenadasGrandes(distanciaM) && distanciaM < 50_000_000) {
              useAppStore.getState().setCoordenadasGrandes(model.modelId, distanciaM)
              avisarCoordenadasGrandes(model.modelId, distanciaM, (texto) =>
                useAppStore.getState().pushToast(texto, { type: 'info', timeout: 15000 }),
              )
            }
            // Verificações da georreferenciação (Info do modelo). Uma falha
            // aqui não pode impedir o modelo de abrir.
            if (dadosGeorref) {
              try {
                guardarVerificacoes(
                  model.modelId,
                  await verificarGeorrefDoModelo(leituraGuardada(model.modelId) ?? null, dadosGeorref, distanciaM),
                )
              } catch (err) {
                console.warn('[georref] verificações falharam', err)
                guardarVerificacoes(model.modelId, null)
              }
            } else {
              guardarVerificacoes(model.modelId, null)
            }
          }
          // Deteção de offset "baked" (LoGeoRef 30): ficheiros sem
          // MapConversion útil (ausente OU a zeros, padrão Revit) mas
          // com coordenadas grandes embutidas nos placements. Com o
          // recentre compensado, o modelo já fica na posição verdadeira
          // do ficheiro automaticamente; a deteção serve para informar
          // a UI e dar posição ao painel Mapa (E=-tx, N=+tz, h=-ty é a
          // zona onde a geometria vive). Comparação por VALOR: um
          // MapConversion a zeros equivale a identidade.
          if (!placementsDiffer(baseline, IDENTITY_PLACEMENT)) {
            const [tx, ty, tz] = recentre
            const dist = Math.hypot(tx, tz)
            // Teto de sanidade: coordenadas embutidas corruptas (ex.: 1e12)
            // mandavam o modelo para o infinito na UI/mapa. O false northing
            // legitimo do UTM chega a 10 000 000 m, por isso 50 000 km da
            // folga suficiente e rejeita apenas lixo evidente.
            const SANITY_DIST_CEILING = 50_000_000
            if (Number.isFinite(dist) && dist > SANITY_DIST_CEILING) {
              console.warn(
                '[useViewer] coordenadas embutidas rejeitadas por sanidade',
                { dist },
              )
            } else if (Number.isFinite(dist) && dist > 100 && !(sitio && dist <= 10_000)) {
              useAppStore.getState().setGeoDetectedBaked(model.modelId, {
                eastings: -tx,
                northings: tz,
                orthogonalHeight: -ty,
                rotationDeg: 0,
                scale: 1,
              })
              // Coordenadas de CRS já embutidas (dezenas de km ou mais): a
              // latitude/longitude do IfcSite não se soma por cima (ficava
              // o dobro). Fica como opção em Informações do modelo.
            } else if (sitio) {
              // Só o IfcSite: põe-se a origem do ficheiro na latitude e
              // longitude declaradas, numa grelha UTM calculada.
              const c = await colocacaoPeloSitio(sitio).catch(() => null)
              if (c) {
                // O placement está na grelha UTM calculada: o CRS do
                // modelo passa a ser essa, mesmo que o ficheiro nomeie
                // outro (IfcProjectedCRS sem IfcMapConversion).
                const registo = registoDaColocacao(c)
                baseline = registo.placement
                useAppStore.getState().setGeoBaseline(model.modelId, baseline)
                useAppStore.getState().setCrsMeta(model.modelId, registo.crs)
                sitio.aplicado = { epsg: c.epsg, convergenciaGraus: c.convergenciaGraus }
              }
            }
          }
          const restore = useAppStore
            .getState()
            .sessionRestoreModels?.find(
              (r) => r.sha256.toLowerCase() === sha256.toLowerCase(),
            )
          const restoreOverride = restore?.placement_override ?? null
          if (restoreOverride) {
            useAppStore
              .getState()
              .setGeoOverride(model.modelId, restoreOverride)
          }
          const override =
            useAppStore.getState().geoOverrides[model.modelId] ?? null
          const placement = override ?? baseline
          applyGeoPlacement(model, placement)
          if (baseline !== IDENTITY_PLACEMENT || override) {
            console.debug(
              '[useViewer] geo placement aplicado',
              {
                source: override
                  ? restoreOverride
                    ? 'session-restore'
                    : 'override'
                  : 'auto',
                placement,
              },
            )
          }
          // Suppress the unused-import warning in this scope:
          // applyGeoTransform still exists as a convenience for
          // callers outside the loader (e.g. unit tests).
          void applyGeoTransform
        } catch (err) {
          console.warn('[useViewer] applyGeoTransform falhou', err)
        }

        // Cache bytes so the user can manually trigger the parallel
        // parser later from the UI even if it's currently disabled.
        if (!isFragmentSource) rawIfc.cacheBytes(model.modelId, rawCopy)
        if (!isFragmentSource && useAppStore.getState().rawIfcEnabled) {
          rawIfc.loadModel(model.modelId).catch((err) => {
            console.warn('[useViewer] rawIfc load failed', err)
          })
        }

        // Pre-compute ancestor paths from the spatial structure so the
        // properties panel can resolve "delta to container" without
        // walking the tree per click.
        try {
          const tree = await model.getSpatialStructure()
          const paths = new Map<number, number[]>()
          const stack: number[] = []
          const walk = (n: typeof tree) => {
            if (n.localId !== null && n.localId !== undefined) {
              paths.set(n.localId, [...stack])
              stack.push(n.localId)
            }
            n.children?.forEach(walk)
            if (n.localId !== null && n.localId !== undefined) {
              stack.pop()
            }
          }
          walk(tree)
          viewerApi.setAncestorPaths(model.modelId, paths)
        } catch (err) {
          console.warn('[useViewer] ancestor path build failed', err)
        }

        // Position the camera around the model bbox when this is a
        // fresh start (replace mode or first model). For federation
        // (add mode with existing models) we keep the current view
        // so the user doesn't lose their navigation context.
        //
        // resolveWorldBox trata da ambiguidade do model.box (local vs
        // mundo): aplicar matrixWorld às cegas mandava a câmara para
        // 2x a posição em modelos georreferenciados.
        // Caixa robusta: ignora outliers (marco de federação à cota 0,
        // textos 3D) que esticavam a bbox e faziam a câmara apontar para
        // espaço vazio, deixando o modelo pequeno e a parecer inclinado.
        const bbox = await resolveRobustFitBox(model)
        // Prima a cache da caixa de geometria (união dos getBoxes) para
        // os consumidores SÍNCRONOS do resolveWorldBox (pegada e centro
        // do mapa, chão do basemap, delta de federação) deixarem de ver
        // o model.box inflado do motor. Em fundo: não atrasa o fit.
        void primeGeometryBoxCache(model)
        if (shouldFit && bbox) {
          const center = new THREE.Vector3()
          bbox.getCenter(center)
          const size = new THREE.Vector3()
          bbox.getSize(size)
          const maxDim = Math.max(size.x, size.y, size.z) || 10
          const d = maxDim * 1.8
          await r.world.camera.controls.setLookAt(
            center.x + d,
            center.y + d,
            center.z + d,
            center.x,
            center.y,
            center.z,
            false,
          )
          r.world.camera.controls.update(0)
        }

        // Without a camera the FragmentsModel cannot run frustum/LOD
        // culling and therefore never spawns any tile mesh.
        model.useCamera(r.world.camera.three as THREE.PerspectiveCamera)

        // Wait past the FragmentsModels internal throttle window
        // (settings.maxUpdateRate) before forcing the first tile pass,
        // otherwise update() returns early without doing anything.
        await new Promise((resolve) => setTimeout(resolve, 100))

        // BIMMesh containers are created by update(true) but the
        // underlying BufferGeometry vertex buffers are streamed from
        // the worker AFTER the update promise resolves. We listen for
        // onViewUpdated (one shot) to know when geometry is atually
        // populated, then run our diagnostic.
        const viewReady = new Promise<void>((resolve) => {
          const handler = () => {
            model.onViewUpdated.remove(handler)
            resolve()
          }
          model.onViewUpdated.add(handler)
          // Safety net: don't wait forever.
          setTimeout(() => {
            model.onViewUpdated.remove(handler)
            resolve()
          }, 3000)
        })
        // Com limite: se o motor nao responder (janela em segundo plano,
        // worker ocupado), a abertura segue e os blocos aparecem quando a
        // vista voltar a desenhar, em vez de ficar presa para sempre.
        await Promise.race([
          r.fragments.core.update(true),
          new Promise((resolve) =>
            setTimeout(() => {
              console.warn('[useViewer] fragments.core.update sem resposta em 8 s; a abertura continua')
              resolve(undefined)
            }, 8000),
          ),
        ])
        await viewReady

        // Wire the paintEngine to this model: patches every Lambert
        // material with the per-vertex `id` lookup shader and registers
        // an onViewUpdated hook so newly-spawned tiles also receive the
        // patch. The patch is a no-op until paintEngine.setItemColors
        // writes to the model's RGBA paint texture; that happens lazily
        // via repaintModel below when a colour layer is active.
        paintEngine.attachPaintToModel(model)
        // Apply the active colour layers (palette + criterion + manual)
        // to the freshly loaded model.
        void viewerApi.repaintOneModel(model.modelId).catch((err) => {
          console.warn('[useViewer] initial repaint failed', err)
        })
        // Honour the spatial-volumes hide preference: IfcSpace /
        // IfcSpatialZone / IfcZone are typically large translucent
        // boxes that obscure the model. Default off in 3D, but the
        // entries stay in the spatial tree / Tabela.
        if (useAppStore.getState().hideSpatialVolumes) {
          try {
            const bucket = await model.getItemsOfCategories([
              /^IfcSpace$/i,
              /^IfcSpatialZone$/i,
              /^IfcZone$/i,
            ])
            const ids: number[] = []
            for (const arr of Object.values(bucket)) {
              if (arr) ids.push(...arr)
            }
            if (ids.length > 0) await model.setVisible(ids, false)
          } catch (err) {
            console.warn('[useViewer] hide spatial volumes failed', err)
          }
        }

        // As IfcSpatialZone entram com geometria desde 2026-09-21 (ver o
        // importador acima). Pintam-se translúcidas, estejam ou não
        // escondidas: quando se desliga o "Esconder spaces" já aparecem
        // como volumes e não como caixas opacas.
        try {
          const zb = await model.getItemsOfCategories([/^IfcSpatialZone$/i])
          const zonas: number[] = []
          for (const arr of Object.values(zb)) if (arr) zonas.push(...arr)
          if (zonas.length > 0) await viewerApi.marcarZonas(model.modelId, zonas)
        } catch (err) {
          console.warn('[useViewer] marcar zonas failed', err)
        }

        // FragmentsModel BIMMeshes carry a boundingSphere that, in some
        // tile updates, leaves geometry frustum-culled even though it
        // is in view. Disabling culling on the model meshes keeps them
        // drawn whenever the tile is alive. Reapplied on every view
        // update because new tiles are spawned with the default.
        const disableCulling = () => {
          model.object.traverse((o) => {
            if ((o as THREE.Mesh).isMesh) o.frustumCulled = false
          })
        }
        disableCulling()
        model.onViewUpdated.add(disableCulling)

        // Outline edges per BIMMesh: gives the BIM-style line drawing
        // on top of the shaded surfaces. Threshold 30 degrees keeps
        // structural edges and drops co-planar tessellation lines.
        // Colour follows the scene background: white edges vanish on
        // a light bg, dark edges vanish on a dark bg. The material is
        // shared across all tile-spawned edges of this model so
        // viewerApi.setBgMode can flip every edge in one .color.set().
        const isLightBg = useAppStore.getState().bgMode === 'light'
        // Mid-grey edges work in both themes against white-default
        // elements: dark enough to bite into white, light enough to
        // not collapse the viewport at zoom against the dark bg.
        // This is the safest universal compromise now that the
        // dark-default tint is disabled (it leaked Fragments
        // setColor memory).
        const edgeMat = new THREE.LineBasicMaterial({
          color: isLightBg ? 0x374151 : 0x6b7280, // slate-700 / slate-500
          transparent: true,
          opacity: isLightBg ? 0.75 : 0.7,
        })
        ;(model as unknown as { __edgeMaterial?: THREE.LineBasicMaterial }).__edgeMaterial = edgeMat
        // Patch the edge material with the per-item visibility shader
        // so hidden items don't render their outlines. The patch is
        // installed once per material; uniforms point at the model's
        // visibility DataTexture (populated by viewerApi when items
        // get hidden/shown).
        patchEdgeMaterial(edgeMat, model.modelId)

        // Async-load the itemIndex (NOT localId; see paint engine
        // commit 66e9ac7 for the same trap) of every spatial container
        // in this model so addEdges can skip generating outline lines
        // for the bbox geometry of IfcSpace / IfcBuildingStorey / etc.
        // The geometry's per-vertex `id` attribute stores the worker's
        // itemIndex (sample.item()), so we must translate localIds via
        // `getItemIdsByLocalIds` before checking against snapshot.id.
        const spatialContainerIds = new Set<number>()
        let spatialIdsReady = false
        void (async () => {
          try {
            const bucket = await model.getItemsOfCategories([
              /^IFCSPACE$/i,
              /^IFCSPATIALZONE$/i,
              /^IFCZONE$/i,
              /^IFCBUILDINGSTOREY$/i,
              /^IFCBUILDING$/i,
              /^IFCSITE$/i,
            ])
            const localIds: number[] = []
            for (const ids of Object.values(bucket)) {
              if (ids) localIds.push(...ids)
            }
            if (localIds.length === 0) {
              spatialIdsReady = true
              return
            }
            const itemIndices = (await (
              model as unknown as {
                threads: {
                  invoke: (
                    modelId: string,
                    method: string,
                    args: unknown[],
                  ) => Promise<number[]>
                }
              }
            ).threads.invoke(model.modelId, 'getItemIdsByLocalIds', [
              localIds,
            ])) as number[]
            for (const idx of itemIndices) {
              if (typeof idx === 'number' && idx >= 0) {
                spatialContainerIds.add(idx)
              }
            }
            spatialIdsReady = true
            // Tell the edge-visibility shader to permanently treat
            // these item indices as hidden. Even if Fragments has the
            // shell visible (typical for IfcSpace), the outline won't
            // render, which is what the user expects.
            setEdgePermanentlyHidden(model.modelId, spatialContainerIds)
            // Expose a console helper so the user can run
            // `__vn2rDebugEdges()` to print per-mesh diagnostic info when
            // wireframe edges of spatial containers refuse to disappear.
            ;(globalThis as unknown as Record<string, unknown>)[
              '__vn2rDebugEdges'
            ] = () => {
              const stats = {
                modelId: model.modelId,
                spatialContainerCount: spatialContainerIds.size,
                meshes: 0,
                meshesWithEdges: 0,
                meshesContainingContainers: 0,
                meshesContainingContainersWithEdges: 0,
                meshesWithNoIdSource: 0,
                lineSegmentsTotal: 0,
                lineSegmentsTopLevel: 0,
                lineSegmentsPatched: 0,
                sampleContainerMeshes: [] as Array<{
                  uuid: string
                  edgeChildren: number
                  idArrLen: number
                  idSample: number[]
                  visible: boolean
                  parentVisible: boolean
                }>,
              }
              model.object.traverse((o) => {
                if ((o as THREE.LineSegments).isLineSegments) {
                  stats.lineSegmentsTotal++
                  const ls = o as THREE.LineSegments
                  if (!ls.parent || !(ls.parent as THREE.Mesh).isMesh) {
                    stats.lineSegmentsTopLevel++
                  }
                  const mat = ls.material as
                    | (THREE.Material & {
                        userData?: { vn2rEdgePatched?: boolean }
                      })
                    | null
                  if (mat?.userData?.vn2rEdgePatched) {
                    stats.lineSegmentsPatched++
                  }
                  return
                }
                const m = o as THREE.Mesh & { __edgesAdded?: boolean }
                if (!m.isMesh) return
                stats.meshes++
                const idArr = getMeshIdArray(m)
                if (!idArr) {
                  stats.meshesWithNoIdSource++
                  return
                }
                const hasEdges = m.children.some(
                  (c) => (c as THREE.LineSegments).isLineSegments,
                )
                if (hasEdges) stats.meshesWithEdges++
                const containsContainer = meshContainsSpatialContainer(
                  idArr,
                  spatialContainerIds,
                )
                if (containsContainer) {
                  stats.meshesContainingContainers++
                  if (hasEdges) {
                    stats.meshesContainingContainersWithEdges++
                    if (stats.sampleContainerMeshes.length < 5) {
                      const edgeChildren = m.children.filter(
                        (c) => (c as THREE.LineSegments).isLineSegments,
                      )
                      stats.sampleContainerMeshes.push({
                        uuid: m.uuid.slice(0, 8),
                        edgeChildren: edgeChildren.length,
                        idArrLen: idArr.length,
                        idSample: Array.from(idArr).slice(0, 6),
                        visible: m.visible,
                        parentVisible: m.parent?.visible ?? true,
                      })
                    }
                  }
                }
              })
              console.log('[vn2rDebugEdges]', stats)
              return stats
            }
            // Re-run once now that we know which meshes to skip.
            // Existing edges on container meshes were already added
            // before we knew, so we also need to remove them.
            model.object.traverse((o) => {
              const mm = o as THREE.Mesh & { __edgesAdded?: boolean }
              if (!mm.isMesh) return
              const idArr = getMeshIdArray(mm)
              // Strip edges in two cases:
              //   1. Mesh has id and contains a spatial container
              //      (its bbox geometry would render as a floating
              //      wireframe rectangle).
              //   2. Mesh has NO id source at all. We can't classify
              //      it and we can't bake a per-vertex id on its edge
              //      geometry: the resulting edges would always look
              //      up texture[0] (visible) and render. These are
              //      precisely the ghost wireframes the user sees.
              const isContainer =
                !!idArr &&
                meshContainsSpatialContainer(idArr, spatialContainerIds)
              const noId = !idArr
              if (!isContainer && !noId) return
              // Strip existing edge children on this mesh.
              for (let i = mm.children.length - 1; i >= 0; i--) {
                const ch = mm.children[i] as THREE.LineSegments
                if (ch.isLineSegments) {
                  mm.remove(ch)
                  try {
                    ch.geometry.dispose()
                  } catch {
                    /* */
                  }
                }
              }
              mm.__edgesAdded = true
            })
          } catch (err) {
            console.warn(
              '[useViewer] spatial container itemIndex fetch failed; edges of spatial volumes will remain visible',
              err,
            )
            spatialIdsReady = true
          }
        })()

        // Malhas (tiles) a que este modelo já pendurou arestas, ou a que o
        // deslizador do limiar as pode pendurar. Quando o motor apaga um
        // tile, liberta a geometria e o material DELE, mas não os filhos
        // que lhe pendurámos: as arestas ficavam na placa gráfica para
        // sempre. Ver libertarArestasDeTilesApagados.
        const malhasVistas = new Set<THREE.Mesh>()
        const addEdges = () => {
          libertarArestasDeTilesApagados(malhasVistas)
          // Honor the user's "Linhas BIM" toggle when seeding new tile
          // edges; if the flag is off we just skip (existing tiles
          // are toggled imperatively by viewerApi.setEdgesVisible).
          const showEdges = useAppStore.getState().edgesVisible
          model.object.traverse((o) => {
            const m = o as THREE.Mesh
            if (!m.isMesh) return
            malhasVistas.add(m)
            if ((m as THREE.Mesh & { __edgesAdded?: boolean }).__edgesAdded) {
              return
            }
            // Source for EdgesGeometry: prefer the live geometry if
            // its position array is still present (cheaper); otherwise
            // fall back to the snapshot taken by
            // installEdgesSnapshotPatch (the array gets deleted on GPU
            // upload so by the time some late tiles get here the live
            // attribute is empty).
            const livePos = m.geometry?.attributes?.position as
              | THREE.BufferAttribute
              | undefined
            const snapshot = (
              m.geometry as THREE.BufferGeometry & {
                __vn2rEdgesSrc?: EdgesSourceCache
              }
            )?.__vn2rEdgesSrc
            // If we know the spatial container ids and this mesh's items
            // are ALL containers, skip: its bbox geometry would render
            // as a floating wireframe rectangle. Use getMeshIdArray to
            // fall back to the live `id` attribute when Fragments
            // populated it via a code path that bypassed our snapshot
            // monkey-patch (the snapshot occasionally misses meshes for
            // spatial containers, which is exactly the case we want to
            // suppress).
            if (spatialIdsReady) {
              const idArr = getMeshIdArray(m)
              if (
                idArr &&
                meshContainsSpatialContainer(idArr, spatialContainerIds)
              ) {
                ;(m as THREE.Mesh & { __edgesAdded?: boolean }).__edgesAdded =
                  true
                return
              }
              // Mesh has NO id source available: we can neither classify
              // it as a spatial container nor bake a per-vertex id on
              // its edge geometry. Without the id, every edge fragment
              // would look up `texture[0]` (always visible) and render
              // unconditionally: exactly the IfcSpace wireframe ghost
              // the user keeps seeing. Skip these meshes entirely.
              // Trade-off: legitimate items without an id attribute
              // (rare; mostly geometry helpers / spatial containers
              // batched by Fragments through a code path that bypasses
              // our setAttribute snapshot) lose their outlines. Better
              // than rendering ghosts.
              if (!idArr) {
                ;(m as THREE.Mesh & { __edgesAdded?: boolean }).__edgesAdded =
                  true
                return
              }
            }
            const haveLive =
              !!livePos && !!livePos.array && livePos.count > 0
            const haveSnapshot = !!snapshot?.position
            if (!haveLive && !haveSnapshot) return
            // Skip planar/quasi-planar tiles. Revit exports section
            // markers / view markers / 2D annotations as IfcAnnotation
            // entities whose geometry is a flat rectangle. Without this
            // skip the user sees ghostly outlined rectangles floating
            // in the model: the "linhas que não correspondem a
            // elementos" complaint.
            try {
              if (!m.geometry.boundingBox) m.geometry.computeBoundingBox()
              const bb = m.geometry.boundingBox
              if (bb) {
                const dx = bb.max.x - bb.min.x
                const dy = bb.max.y - bb.min.y
                const dz = bb.max.z - bb.min.z
                const maxDim = Math.max(dx, dy, dz)
                const minDim = Math.min(dx, dy, dz)
                if (maxDim > 0 && minDim < 0.001 * maxDim) {
                  // Planar: mark as "edges intentionally skipped" so
                  // we don't reconsider it on every onViewUpdated.
                  ;(m as THREE.Mesh & { __edgesAdded?: boolean }).__edgesAdded =
                    true
                  return
                }
              }
            } catch {
              /* fall through and try edges anyway */
            }
            try {
              const threshold = useAppStore.getState().edgeThreshold
              let edgesGeo: THREE.EdgesGeometry
              // Source position + id arrays we'll need to bake an `id`
              // attribute on the edge geometry (so the edge shader can
              // discard fragments belonging to hidden items).
              const srcPos = haveLive
                ? (livePos!.array as Float32Array)
                : snapshot!.position!
              // Prefer the snapshot id (survives GPU upload, matches
              // exact source positions used by EdgesGeometry); fall
              // back to the live attribute when the snapshot wasn't
              // captured for this mesh.
              let srcId: Float32Array | null = snapshot?.id ?? null
              if (!srcId) {
                const liveIdAttr = m.geometry?.attributes?.id as
                  | THREE.BufferAttribute
                  | undefined
                const liveArr = liveIdAttr?.array as
                  | ArrayLike<number>
                  | undefined
                if (liveArr && liveArr.length > 0) {
                  srcId = new Float32Array(liveArr)
                }
              }
              if (haveLive) {
                edgesGeo = new THREE.EdgesGeometry(m.geometry, threshold)
              } else {
                // Build a temp geometry from the snapshot so EdgesGeometry
                // has something to walk.
                const tmp = new THREE.BufferGeometry()
                tmp.setAttribute(
                  'position',
                  new THREE.BufferAttribute(snapshot!.position!, 3),
                )
                if (snapshot!.index) {
                  tmp.setIndex(new THREE.BufferAttribute(snapshot!.index, 1))
                }
                edgesGeo = new THREE.EdgesGeometry(tmp, threshold)
                tmp.dispose()
              }
              // Bake per-line-endpoint id attribute so the edge shader can
              // look up visibility per item (filters phantom outlines of
              // items hidden via Esconder / Isolar).
              if (srcId) {
                const edgePos = edgesGeo.attributes.position
                  ?.array as Float32Array | undefined
                if (edgePos) {
                  const ids = buildEdgeIdAttribute(
                    edgePos as Float32Array,
                    srcPos,
                    srcId,
                  )
                  edgesGeo.setAttribute(
                    'id',
                    new THREE.BufferAttribute(ids, 1, false),
                  )
                }
              }
              const edges = new THREE.LineSegments(edgesGeo, edgeMat)
              edges.frustumCulled = false
              edges.visible = showEdges
              m.add(edges)
              ;(m as THREE.Mesh & { __edgesAdded?: boolean }).__edgesAdded =
                true
            } catch (err) {
              console.warn('[useViewer] addEdges skipped a mesh', err)
            }
          })
        }
        addEdges()
        model.onViewUpdated.add(addEdges)
        // Sync edge visibility with the parent BIMMesh's visibility
        // each tile update. Without this, hiding IfcSpaces (or any
        // other elements) leaves their edge LineSegments drawn:
        // user sees floating wireframe rectangles with nothing
        // inside. Children of an invisible mesh in three.js still
        // render unless we set them invisible explicitly.
        const syncEdgeVisibility = () => {
          model.object.traverse((o) => {
            const m = o as THREE.Mesh
            if (!m.isMesh) return
            for (const child of m.children) {
              if ((child as THREE.LineSegments).isLineSegments) {
                child.visible = m.visible && useAppStore.getState().edgesVisible
              }
            }
          })
        }
        syncEdgeVisibility()
        model.onViewUpdated.add(syncEdgeVisibility)
        // Refresh ghost overlay when new tiles arrive: streamed-in
        // items would otherwise render solid until the next selection
        // change. Fire-and-forget: no need to block the tile update.
        model.onViewUpdated.add(() => {
          // refreshGhost só recalcula quando há fantasma pedido (o da
          // selecção ou o fixo, por modelo ou pelas Lentes).
          void viewerApi.refreshGhost()
        })
        // Tile spawn handling is now done inside paintEngine itself:
        // attachPaintToModel hooks model.onViewUpdated to re-patch the
        // materials of newly-spawned tiles. The colour data lives in a
        // per-model DataTexture sampled by the shader, so once the
        // shader is patched the colour applies regardless of how many
        // times Fragments re-tiles. No app-level repaint loop needed.

        // (Dedup removeModel moved up, before the geo block; see the
        // comment next to registerModel.)
        addModel({
          id: model.modelId,
          fileName: displayName,
          schemaVersion: schema,
          visible: true,
          entityCount: 0,
          sha256,
          sourceFormat: isFragmentSource ? 'frag' : 'ifc',
          rawIfcAvailable: !isFragmentSource,
        })
        // Contar as entidades agora que o modelo existe. É uma
        // chamada só e não lê atributos nenhuns: o Fragments devolve
        // os ids por classe. Sem isto o contador nascia a zero e
        // nunca mais mudava, e quem o lesse (barra de estado,
        // Documento) dizia "0 entidades" sobre um modelo cheio.
        void (async () => {
          try {
            const baldes = await model.getItemsOfCategories([/.*/])
            let total = 0
            for (const ids of Object.values(baldes)) total += ids?.length ?? 0
            useAppStore.getState().setModelEntityCount(model.modelId, total)
          } catch {
            // Fica a zero, como estava. Não vale a pena estragar o
            // carregamento por causa de um contador.
          }
        })()

        // Persist the FS Access handle (if the picker provided one)
        // so a future Open Session can re-read the file without
        // prompting again. Best-effort: failures are harmless.
        // Note: handle still points at the .ifczip the user picked;
        // when re-read, we'll go through the same unzip path again.
        if (handle && !isFragmentSource) {
          void rememberHandle({
            sha256,
            filename: file.name,
            handle,
          })
        }
        setStage(null)
        return { ok: true, modelId: model.modelId }
      } catch (err) {
        console.error('[useViewer] loadIfcFile failed', err)
        const erro = err instanceof Error ? err.message : String(err)
        setError(erro)
        setStage(null)
        // O motor já tinha criado o modelo e algo falhou depois: sai, em
        // vez de ficar desenhado sem entrada na lista.
        if (
          modeloCriado &&
          !useAppStore.getState().models.some((m) => m.id === modeloCriado?.modelId)
        ) {
          const id = modeloCriado.modelId
          r.models.delete(id)
          if (viewerApi.getModel(id)) {
            await viewerApi.disposeModel(id).catch(() => {})
          } else {
            modeloCriado.object.removeFromParent()
            await modeloCriado.dispose().catch(() => {})
          }
        }
        return { ok: false, erro }
      } finally {
        setLoading(false)
      }
    },
    [addModel, removeModel, ready],
  )

  // Os pedidos de abertura entram em fila: um de cada vez, pela ordem
  // em que chegaram. Em paralelo, dois "Substituir" seguidos (um grande
  // a converter e outro logo a seguir) acabavam com os dois modelos
  // federados, porque o segundo só descartava o que já lá estava.
  const filaRef = useRef<Promise<unknown>>(Promise.resolve())
  const loadIfcFile = useCallback(
    (file: File, options?: LoadIfcOptions): Promise<ResultadoDaAbertura> => {
      const vez = filaRef.current.then(() => carregarAgora(file, options))
      filaRef.current = vez.catch(() => undefined)
      return vez
    },
    [carregarAgora],
  )

  // Publish the loader through the singleton so non-canvas surfaces
  // (sidebar, drag-drop) can trigger an IFC load without a ref into
  // this component. Mirror of the highlighter/world registration.
  useEffect(() => {
    viewerApi.setIfcLoader(loadIfcFile)
    return () => {
      viewerApi.setIfcLoader(null)
    }
  }, [loadIfcFile])

  // Onda B da federação geo: quando o utilizador mexe nos inputs de
  // override no painel "Posição" do sidebar, o store actualiza
  // geoOverrides; aqui re-aplicamos o placement a CADA modelo
  // carregado. Override presente vence sobre baseline. O loop é
  // O(n_modelos) por mudança, irrelevante na escala típica de
  // federação (<20 modelos).
  const geoOverrides = useAppStore((s) => s.geoOverrides)
  const geoBaselines = useAppStore((s) => s.geoBaselines)
  useEffect(() => {
    const r = refsRef.current
    if (!r) return
    podarModelosDescartados(r.models)
    for (const [modelId, model] of r.models) {
      const override = geoOverrides[modelId]
      const baseline = geoBaselines[modelId] ?? IDENTITY_PLACEMENT
      applyGeoPlacement(model, override ?? baseline)
    }
    // Pump the renderer so the new transform is visible immediately.
    r.world.renderer?.three.render(r.world.scene.three, r.world.camera.three)
    void r.fragments.core.update(true)
  }, [geoOverrides, geoBaselines])

  return { containerRef, loading, stage, error, loadIfcFile }
}
